import React, { ReactElement } from 'react';
import { DataGrid, Overlay } from 'src/common-ui';
import { DataGridProps, Gridable } from 'src/common-ui/components/DataGrid/DataGrid';
import SubheaderDropdown from 'src/components/Subheader/SubheaderDropdown';
import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { summaryContainer, summarySubheader, summaryContent } from './QuickReconcile.styles';
import { StateProjection } from './QuickReconcile.selectors';
import Axios from 'src/services/axios';
import { CancelTokenSource, default as AxiosStatic } from 'axios';
import { ColDef } from '@ag-grid-community/core';
import { RegionItem } from 'src/worker/pivotWorker.types';
import { ClientDataApi } from 'src/services/configuration/codecs/confdefnView';
import { ErrorBoundary } from 'src/components/ErrorBoundary/ErrorBoundary';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'src/services/configuration/codecs/confdefnComponents';

export interface PerformanceDetails {
  columnDefs: ColDef[];
  data: Gridable[];
}

interface QuickReconcileProps {
  groupByDefn: string;
  configApi: ClientDataApi;
  floorsetApi: ClientDataApi;
  subheaderCustomEl: JSX.Element | undefined;
}

export interface FloorsetViewItem extends TenantConfigViewItem {
  id: string;
}

export interface FunctionProps {
  onShowView(): void;
  onUpdateView(): void;
  onRefetchData(): void;
  onDestroy(): void;
  onUpdateGroupBy(newGroup: number): void;
  onUpdateFloorset(newFloorset: string, selectedIndex: number): void;
  onReceiveFloorSetQR(floorSetsData: FloorsetViewItem[]): void;
}

export type Props = StateProjection & QuickReconcileProps & FunctionProps;

export interface State {
  filterLoaded: boolean;
}

export default class QuickReconcile extends React.Component<Props, State> {
  canceler: CancelTokenSource;
  constructor(props: Props) {
    super(props);
    this.canceler = AxiosStatic.CancelToken.source();
    this.state = {
      filterLoaded: false,
    };
  }

  componentDidMount() {
    this.onRefetchData();
  }

  componentDidUpdate() {
    this.props.onUpdateView();
  }

  componentWillUnmount() {
    this.props.onDestroy();
  }

  async onRefetchData() {
    this.setState({
      filterLoaded: false,
    });

    const { onUpdateGroupBy, onUpdateFloorset, onReceiveFloorSetQR } = this.props;
    const reqs1 = [
      Axios.get(this.props.configApi.url, {
        params: this.props.configApi.params,
        cancelToken: this.canceler.token,
      }),
      Axios.get(this.props.floorsetApi.url, {
        headers: this.props.floorsetApi.headers,
        params: this.props.floorsetApi.params,
        cancelToken: this.canceler.token,
      }),
    ];

    Promise.all(reqs1).then((response) => {
      const floorSetsData = response[1].data.data.map((data: RegionItem) => ({ ...data, text: data.description }));
      this.setState(
        {
          filterLoaded: true,
        },
        () => {
          onReceiveFloorSetQR(floorSetsData);
          onUpdateGroupBy(0);
          onUpdateFloorset(floorSetsData[0].id, 0);
        }
      );
    });
  }

  handleGroupByClickDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { onUpdateGroupBy, groupBy } = this.props;
    if (+event.target.value !== groupBy.selection) {
      onUpdateGroupBy(+event.target.value);
    }
  };

  handleFloorsetClickDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { onUpdateFloorset, floorSetsData, selectedFloorsetIndex } = this.props;
    const index = +event.target.value;
    if (selectedFloorsetIndex !== index) {
      const selectedFloorset = floorSetsData[index].id;
      onUpdateFloorset(selectedFloorset, index);
    }
  };

  render() {
    let mainContent: ReactElement | null = null;
    let groupByDropDown: ReactElement | null = null;
    let floorsetDropDown: ReactElement | null = null;
    const { groupBy, floorsetEnabled } = this.props;
    if (this.state.filterLoaded) {
      groupByDropDown = (
        <SubheaderDropdown
          defaultSelection={0}
          selection={this.props.groupBy.selection}
          options={groupBy.options}
          label={'Group By: '}
          handleChangeOnDropdown={this.handleGroupByClickDropdown}
        />
      );

      floorsetDropDown = floorsetEnabled ? (
        <SubheaderDropdown
          defaultSelection={0}
          selection={this.props.selectedFloorsetIndex}
          options={this.props.floorSetsData}
          label={'Floorset: '}
          handleChangeOnDropdown={this.handleFloorsetClickDropdown}
        />
      ) : null;
    }

    if (this.props.loaded) {
      const { gridData, colDefs, frameworkComponents, isPrintMode } = this.props;

      const gridOptions: DataGridProps = {
        isPrintMode,
        columnDefs: colDefs,
        frameworkComponents,
        data: gridData,
        loaded: true,
        rowHeight: 30,
        treeColumnDefinition: undefined,
        className: 'summaryGrid',
      };

      mainContent = <DataGrid {...gridOptions} />;
    }

    if (this.props.error) {
      return (
        <ErrorBoundary
          error={{
            type: ComponentErrorType.view,
            name: ConfDefnComponentType.quickReconcile,
            message: 'An error occurred during load.',
          }}
        />
      );
    }

    return (
      <div className={summaryContainer}>
        <Overlay type="loading" visible={!!this.props.isViewDataLoading} />
        <div className={summarySubheader}>
          <div className="dropdown">{floorsetDropDown}</div>
          <div className="dropdown">{groupByDropDown}</div>
        </div>
        <div className={summaryContent}>{mainContent}</div>
      </div>
    );
  }
}
